import { DateRange, IDateRangeShortcut } from '@blueprintjs/datetime';
import moment from 'moment';
import React, { FC, useEffect, useRef, useState } from 'react';
import { EShortcuts, IFilterType } from '../../../../../../types/ProfileTypes';
import { ProfileUtils } from '../../../utils/ProfileUtils';
import { StyledCustomDateTimePicker } from '../styled';

interface ICustomDateRange {
  filter: IFilterType;
  isStacked?: boolean;
  onTable?: boolean;
  onChange: (value: any) => void;
  disabled: boolean;
  shortcuts?: any[];
  showLabel?: boolean;
  isQueryFilterInForm?: boolean;
  widthComponent?: number;
  calculateMaxTime: (calculateMaxTime: string) => Date;
}

export const CustomDateRange: FC<ICustomDateRange> = ({
  isStacked,
  onTable,
  filter,
  onChange,
  disabled,
  shortcuts,
  showLabel,
  isQueryFilterInForm,
  widthComponent,
  calculateMaxTime
}) => {
  const currentFilter = useRef<IFilterType>(filter);
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState(-1);
  const [dateType, setDateType] = useState<EShortcuts | ''>('');
  const { value: valueFilter, dateType: dateTypeFilter, subLabels } = filter;

  useEffect(() => {
    setSelectedShortcutIndex(
      !!dateType && !!shortcuts?.length ? shortcuts.findIndex((item: any) => item?.type === dateType) : -1
    );
  }, [shortcuts, dateType]);

  const generalOnChange = ([start, end]: DateRange) => {
    let value = [start, end];
    if (!start || !end) {
      value = [
        start ?? ProfileUtils.includeTimeToDate(end, 24, 0),
        end ?? ProfileUtils.includeTimeToDate(start, 23, 59)
      ];
    }

    onChange({
      ...currentFilter.current,
      value
    });
  };

  const includeDateType = (dateType: EShortcuts) => {
    currentFilter.current = { ...currentFilter.current, dateType };
    setDateType(dateType);
  };

  const getValue = (current: DateRange, dateType?: EShortcuts | '') => {
    let value: DateRange = current ?? [null, null];

    if (current && !!shortcuts?.length) {
      const isNotDateType = !dateType || dateType === EShortcuts.USER_DEFINED;
      if (!isNotDateType) {
        const shortcut = shortcuts.find((item: any) => item?.type === dateType);
        const valueCache = ProfileUtils.convertDateRange(shortcut?.dateRange ?? [null, null]);
        const compareRange = ProfileUtils.compareRange(value as DateRange, valueCache);
        if (valueCache && !compareRange) {
          generalOnChange(valueCache);
          return ProfileUtils.convertDateRange(valueCache);
        }
      } else if (ProfileUtils.isDateRange(value)) {
        value = ProfileUtils.convertDateRange(value as DateRange);
      }
    }
    return value;
  };

  const [value, setValue] = useState<DateRange>(getValue(valueFilter, dateTypeFilter ?? ''));

  useEffect(() => {
    const dt = dateTypeFilter ?? '';
    currentFilter.current = filter;
    setDateType(dt);
    setValue(getValue(valueFilter, dt));
  }, [filter]);

  const onShortcutChange = (shortcut: IDateRangeShortcut, index: number) => {
    if (!!shortcuts?.length && currentFilter.current.hasOwnProperty('dateType')) {
      const shortcutSelected = shortcuts[index];
      const dateTypeProp = shortcutSelected?.type ?? '';
      const toDay = moment().toDate();
      dateTypeProp === EShortcuts.USER_DEFINED && resetTime([toDay, toDay]);
      includeDateType(dateTypeProp);
    }
  };

  const onDayClick = (day: Date, modifiers: any, e: React.MouseEvent<HTMLDivElement>) => {
    if (!!shortcuts?.length && currentFilter.current.hasOwnProperty('dateType')) {
      const existUserDefined = shortcuts.find((item: any) => item?.type === EShortcuts.USER_DEFINED);
      if (existUserDefined) {
        includeDateType(EShortcuts.USER_DEFINED);
      }
    }
  };

  const resetTime = ([start, end]: DateRange) => {
    const newValue = [ProfileUtils.includeTimeToDate(start, 0, 0), ProfileUtils.includeTimeToDate(end, 23, 59)];
    setValue(newValue as DateRange);
    currentFilter.current = {
      ...currentFilter.current,
      value: newValue
    };
  };

  return (
    <StyledCustomDateTimePicker
      isStacked={!!isStacked}
      upperCaseFormat
      allowSingleDayRange
      onTable={onTable}
      id={`custom-date-range-${filter.field}`}
      inline
      dateType={!!filter?.timePrecision ? 'DATETIME' : 'DATE'}
      margin={'0'}
      layer={{
        inputWidth: showLabel ? 8 : 12,
        labelOrientation: 'start',
        labelWidth: showLabel ? 4 : undefined
      }}
      disabled={disabled}
      noLabel={!showLabel}
      fill
      format={!!filter?.timePrecision ? 'MM/DD/YYYY hh:mm a' : 'MM/DD/YYYY'}
      value={value}
      tipLabel={!onTable && !isQueryFilterInForm ? filter.label : undefined}
      label={showLabel ? filter.label : ''}
      onChange={generalOnChange}
      useAmPm={filter?.useAmPm}
      precision={filter?.timePrecision}
      shortcuts={shortcuts}
      maxTime={filter.calculateMaxTime ? calculateMaxTime(filter.calculateMaxTime) : undefined}
      refWidth={widthComponent ?? 0}
      onShortcutChange={onShortcutChange}
      selectedShortcutIndex={selectedShortcutIndex}
      subLabels={!onTable ? subLabels : undefined}
      dayPickerProps={{
        onDayClick
      }}
    />
  );
};
